import React, { Component } from 'react';
import Dropzone from 'react-dropzone';
import { withTranslation } from 'react-i18next';
import { UploadIcon } from '@components-library/Icons/iconsComponents/UploadIcon/UploadIcon';
import { IDropzoneProps, IDropzoneState } from '@components-shared/DropZone/types';
import CONSTANTS from '@components-shared/constants';
import { keyMap, KeyMapKeys } from '@locales/lokalise/keyMap';
import { FileValidationService, fileValidationService } from '@utils/library/fileValidation.service';
import type { validFileType } from '@utils/library/fileValidation.service';
import { fetchApi } from 'api/fetchApi';
import cn from 'classnames';
import { FlexiblePreloader } from 'components/shared/preloader';
import { handleError } from 'utils/errors';
import { NotificationDialog } from '../modal';

class DropZone extends Component<IDropzoneProps, IDropzoneState> {
  constructor(props: IDropzoneProps) {
    super(props);
    this.state = {
      loading: false,
    };
  }

  onDrop = async (files: File[]) => {
    const { accept, params, onChange, name, extraApiParams } = this.props;
    const file = files[0];

    let acceptedFileValidationService = fileValidationService;

    if (accept) {
      acceptedFileValidationService = FileValidationService.init(accept.split(', ') as validFileType[]);
    }

    const isValid = await acceptedFileValidationService.isValidFiles(files);

    if (!isValid) {
      return;
    }

    const { api, onDropHandler, getFileModel, onUploadProgress, onUploadError, onBeforeUploadStarted, disableLoader } =
      params || {};

    if (onBeforeUploadStarted) {
      onBeforeUploadStarted(file);
    }

    if (!file || !api) {
      return null;
    }

    const uploadApi = extraApiParams
      ? api.includes('?')
        ? `${api}&${extraApiParams}`
        : `${api}?${extraApiParams}`
      : api;

    const formData = new FormData();

    formData.append('file', file);

    if (!disableLoader) {
      this.setState({ loading: true });
    }

    try {
      const response = await fetchApi.post(uploadApi, formData, {
        headers: { 'content-type': 'multipart/form-data' },
        onUploadProgress(progressEvent) {
          if (onUploadProgress) {
            onUploadProgress(progressEvent);
          }
        },
      });

      if (!disableLoader) {
        this.setState({ loading: false });
      }

      NotificationDialog.show(keyMap.settingsDocsFormUploadSuccessMessage);

      if (onDropHandler) {
        onDropHandler(file, response);
      }

      if (getFileModel) {
        getFileModel(response);
      }

      if (onChange) {
        onChange(name);
      }
    } catch (error) {
      if (!disableLoader) {
        this.setState({ loading: false });
      }

      if (onUploadError) {
        onUploadError(error.response.data);
      }

      handleError(this.props.t(keyMap.settingsDocsFormUploadErrorMessage), error);
    }
  };

  render() {
    const { loading } = this.state;
    const { attributes, hintMessage, accept, maxSize, title, description, t } = this.props;
    const acceptString = accept || CONSTANTS.ACCEPTED_FILE_TYPE;
    const isDropzoneDisabled = attributes ? attributes.disabled || loading : false;

    return (
      <Dropzone accept={acceptString} onDrop={this.onDrop} maxSize={maxSize || 52428800} disabled={isDropzoneDisabled}>
        {({ getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject }) => {
          const additionalClasses = cn('docs-dropzone__area', {
            'dropzone--active': isDragActive,
            'dropzone--accept': isDragAccept,
            'dropzone--reject': isDragReject,
            'dropzone--disabled': isDropzoneDisabled,
          });

          return (
            <>
              {loading ? (
                <FlexiblePreloader />
              ) : (
                <div {...getRootProps({ className: additionalClasses })}>
                  <div className="docs-dropzone__title-wrapper">
                    <UploadIcon size={16} className="docs-dropzone__icon" />

                    {title && <div className="docs-dropzone__title">{t(title as KeyMapKeys)}</div>}
                  </div>

                  {description && <div className="docs-dropzone__description">{t(description as KeyMapKeys)}</div>}

                  {hintMessage && <div className="docs-dropzone__hint">{t(hintMessage as KeyMapKeys)}</div>}

                  <input {...getInputProps()} data-autotest={attributes?.dataAutoTest} />
                </div>
              )}
            </>
          );
        }}
      </Dropzone>
    );
  }
}

export default withTranslation()(DropZone);
